import { createAction } from 'redux-act';
import { toastr } from 'react-redux-toastr';

import { firebaseError } from 'utils';
import CONSTANTS from 'utils/constants';
import { checkUserData } from './auth';
import { createEntity as createEntityLog } from './logs';
import {
  fetchCollection,
  fetchDocument,
  createDocument,
  archiveDocument,
  updateDocument,
} from '../api';

const defaultActions = ({
  ENTITIES,
  ENTITY,
  Entity,
  entityName,
  ENTITIES_COLLECTION,
  fetchCollectionByStandardUser,
  updateEntityByUser,
}) => {
  const ACTIONS_NAMES_DICT = {
    FETCH_DATA_INIT: `${ENTITIES}_FETCH_DATA_INIT`,
    FETCH_DATA_SUCCESS: `${ENTITIES}_FETCH_DATA_SUCCESS`,
    FETCH_DATA_FAIL: `${ENTITIES}_FETCH_DATA_FAIL`,
    CREATE_ENTITY_INIT: `${ENTITIES}_CREATE_${ENTITY}_INIT`,
    CREATE_ENTITY_SUCCESS: `${ENTITIES}_CREATE_${ENTITY}_SUCCESS`,
    CREATE_ENTITY_FAIL: `${ENTITIES}_CREATE_${ENTITY}_FAIL`,

    UPDATE_ENTITY_INIT: `${ENTITIES}_UPDATE_${ENTITY}_INIT`,
    UPDATE_ENTITY_SUCCESS: `${ENTITIES}_UPDATE_${ENTITY}_SUCCESS`,
    UPDATE_ENTITY_FAIL: `${ENTITIES}_UPDATE_${ENTITY}_FAIL`,

    ARCHIVE_ENTITY_INIT: `${ENTITIES}_ARCHIVE_${ENTITY}_INIT`,
    ARCHIVE_ENTITY_SUCCESS: `${ENTITIES}_ARCHIVE_${ENTITY}_SUCCESS`,
    ARCHIVE_ENTITY_FAIL: `${ENTITIES}_ARCHIVE_${ENTITY}_FAIL`,

    CLEAN_UP: `${ENTITIES}_CLEAN_UP`,
    CLEAR_DATA_LOGOUT: `${ENTITIES}_CLEAR_DATA_LOGOUT`,
  };

  const ACTIONS_DICT = Object.keys(ACTIONS_NAMES_DICT).reduce(
    (accumulator, currentKey) => {
      const currentValue = ACTIONS_NAMES_DICT[currentKey];
      accumulator[currentKey] = createAction(currentValue);
      return accumulator;
    },
    {}
  );

  const ENTITY_ACTIONS = Object.keys(ACTIONS_NAMES_DICT).reduce(
    (accumulator, currentKey) => {
      const key = ACTIONS_NAMES_DICT[currentKey];
      const value = ACTIONS_DICT[currentKey];
      accumulator[key] = value;
      return accumulator;
    },
    {}
  );

  /**
   * Fetch Entities
   * @param {*} entityId
   * @returns
   */
  const fetchEntities = (entityId = '') => {
    return async (dispatch, getState) => {
      dispatch(checkUserData());
      const { role } = getState().auth.userData;

      dispatch(ACTIONS_DICT.FETCH_DATA_INIT());

      if (entityId) {
        let entity;
        try {
          entity = await fetchDocument(ENTITIES_COLLECTION, entityId);
        } catch (error) {
          toastr.error('', error);
          return dispatch(ACTIONS_DICT.FETCH_DATA_FAIL({ error }));
        }

        if (!entity) {
          const errorMessage = 'Entity not available';
          toastr.error('', errorMessage);
          return dispatch(
            ACTIONS_DICT.FETCH_DATA_FAIL({ error: errorMessage })
          );
        }

        const entities = getState()[ENTITIES_COLLECTION].data;
        entities.push(entity);

        return dispatch(
          ACTIONS_DICT.FETCH_DATA_SUCCESS({
            data: entities,
          })
        );
      }

      let entities;
      try {
        if (CONSTANTS.IS_USER_ROLE_ADMIN[role]) {
          entities = await fetchCollection(ENTITIES_COLLECTION);
        } else if (fetchCollectionByStandardUser) {
          entities = await fetchCollectionByStandardUser(ENTITIES_COLLECTION);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        toastr.error('', error);
        return dispatch(ACTIONS_DICT.FETCH_DATA_FAIL({ error }));
      }

      return dispatch(
        ACTIONS_DICT.FETCH_DATA_SUCCESS({
          data: entities,
        })
      );
    };
  };

  const createEntity = (entityData, preCall, hooks) => {
    return async (dispatch, getState) => {
      dispatch(ACTIONS_DICT.CREATE_ENTITY_INIT());
      const { locale } = getState().preferences;

      let entityDataAfterPreCall = entityData;
      let entityId = null;
      if (preCall) {
        try {
          entityDataAfterPreCall = await preCall(entityData);
          entityId = entityDataAfterPreCall.id;
        } catch (error) {
          const errorMessage = firebaseError(error.message, locale);
          toastr.error('', errorMessage);
          return dispatch(
            ACTIONS_DICT.CREATE_ENTITY_FAIL({
              error: errorMessage,
            })
          );
        }
      }

      const createEntityDbTask = createDocument(
        ENTITIES_COLLECTION,
        entityId,
        entityDataAfterPreCall
      );

      try {
        await createEntityDbTask.then((docRef) => {
          if (docRef && docRef.id) {
            entityId = docRef.id;
          }
        });

        // Add log
        const authorId = getState().auth.userData.id;
        toastr.success('', `${Entity} created successfully`);

        const activityLog = {
          payload: entityDataAfterPreCall,
          entityId,
          authorId,
          entityClass: entityName,
          action: 'CREATE',
          type: 'SYSTEM',
          createdAt: new Date().toISOString(),
        };
        dispatch(createEntityLog(activityLog, { showToaster: false }));
      } catch (error) {
        const errorMessage = firebaseError(error.code, locale);
        toastr.error('', errorMessage);
        return dispatch(
          ACTIONS_DICT.CREATE_ENTITY_FAIL({
            error: errorMessage,
          })
        );
      }

      if (hooks && hooks.postActionSuccess && hooks.postActionSuccess.hook) {
        const { hook, params } = hooks.postActionSuccess;
        params.entityId = entityId;
        dispatch(hook(params));
      }
      toastr.success('', `${Entity} created successfully`);
      const entityToState = {
        ...entityDataAfterPreCall,
        id: entityDataAfterPreCall.id || entityId,
      };
      return dispatch(
        ACTIONS_DICT.CREATE_ENTITY_SUCCESS({ entity: entityToState })
      );
    };
  };

  /**
   *
   * @param {*} entityData
   * @param {*} hooks postActionSuccess
   * @returns
   */
  const updateEntity = (entityData, hooks) => {
    return async (dispatch, getState) => {
      dispatch(ACTIONS_DICT.UPDATE_ENTITY_INIT());
      const { locale } = getState().preferences;
      const { role } = getState().auth.userData;

      const { id } = entityData;
      try {
        let updateDbTask = null;
        if (CONSTANTS.IS_USER_ROLE_INSTALLER[role] && updateEntityByUser) {
          updateDbTask = updateEntityByUser(id, entityData);
        } else {
          updateDbTask = updateDocument(ENTITIES_COLLECTION, id, entityData);
        }
        await Promise.all([updateDbTask]);
      } catch (error) {
        const errorMessage = firebaseError(error.code, locale);
        toastr.error('', errorMessage);
        return dispatch(
          ACTIONS_DICT.UPDATE_ENTITY_FAIL({
            error: errorMessage,
          })
        );
      }
      toastr.success('', `${Entity} updated successfully`);
      // Add log
      try {
        const authorId = getState().auth.userData.id;
        const activityLog = {
          payload: entityData,
          entityId: id,
          authorId,
          entityClass: entityName,
          action: 'UPDATE',
          type: 'SYSTEM',
          createdAt: new Date().toISOString(),
        };
        dispatch(createEntityLog(activityLog, { showToaster: false }));
      } catch (error) {
        const errorMessage = firebaseError(error.code, locale);
        toastr.error('', errorMessage);
      }

      if (hooks && hooks.postActionSuccess && hooks.postActionSuccess.hook) {
        const { hook, params } = hooks.postActionSuccess;
        dispatch(hook(params));
      }
      return dispatch(
        ACTIONS_DICT.UPDATE_ENTITY_SUCCESS({ data: entityData, id })
      );
    };
  };

  const archiveEntity = (entityId, preCall) => {
    return async (dispatch, getState) => {
      dispatch(ACTIONS_DICT.ARCHIVE_ENTITY_INIT());
      const { locale } = getState().preferences;

      if (preCall) {
        try {
          await preCall(entityId);
        } catch (error) {
          const errorMessage = firebaseError(error.message, locale);
          toastr.error('', errorMessage);
          return dispatch(
            ACTIONS_DICT.ARCHIVE_ENTITY_FAIL({
              error: errorMessage,
            })
          );
        }
      }

      const updateDbTask = archiveDocument(ENTITIES_COLLECTION, entityId);
      try {
        await Promise.all([updateDbTask]);
      } catch (error) {
        const errorMessage = firebaseError(error.code, locale);
        toastr.error('', errorMessage);
        return dispatch(
          ACTIONS_DICT.ARCHIVE_ENTITY_FAIL({
            error: errorMessage,
          })
        );
      }

      toastr.success('', `${Entity} archived successfully`);
      // Add log
      try {
        const authorId = getState().auth.userData.id;
        const activityLog = {
          payload: null,
          entityId,
          authorId,
          entityClass: entityName,
          action: 'ARCHIVE',
          type: 'SYSTEM',
          createdAt: new Date().toISOString(),
        };
        dispatch(createEntityLog(activityLog, { showToaster: false }));
      } catch (error) {
        const errorMessage = firebaseError(error.code, locale);
        toastr.error('', errorMessage);
      }

      dispatch(ACTIONS_DICT.ARCHIVE_ENTITY_SUCCESS({ id: entityId }));
      return true;
    };
  };

  const entitiesCleanUp = () => (dispatch) => dispatch(ACTIONS_DICT.CLEAN_UP());

  const clearEntitiesDataLogout = () => {
    return (dispatch) => {
      dispatch(ACTIONS_DICT.CLEAR_DATA_LOGOUT());
    };
  };

  return {
    ACTIONS_NAMES_DICT,
    ENTITY_ACTIONS,
    fetchEntities,
    createEntity,
    updateEntity,
    archiveEntity,
    entitiesCleanUp,
    clearEntitiesDataLogout,
  };
};

export default defaultActions;
